import { buildLicenseCode } from "./buildLicenseCode"
import { type NormalizedLicenseData } from "./normalizeLicenseData"

type GenerateLicenseMarkdownOptions = {
  licenseCode?: string
  subListStyle?: "roman" | "decimal"
  listStyle?: "ordered" | "unordered"
}

const ACCESS_TEXT: Record<string, string> = {
  accessDirect:
    "Direct sharing is permitted, in which the full dataset will be transferred directly to the recipient.",
  accessCollaborative:
    "Collaborative sharing is permitted, during which the provider and recipient will work together collaboratively to do a joint analysis on the data. These data may be combined with other data held by the collaborator to do collaborative meta-analysis of the combined dataset.",
  accessFederated:
    "Federated analysis is permitted, for which the provider and recipient will each perform the same agreed analysis on their own dataset, and the aggregate findings and/or analysis outputs will be combined and/or reported together.",
  accessTRE:
    "The data are available for analysis on a Trusted research environment, which is a a secure digital/online platform where analysis of the dataset can be undertaken by authorised users. Download and viewing of the dataset will not be possible. Only results of the analysis will be available for download and there may be a requirement for review of these results by the data provider prior to download."
}

const COMMERCIAL_TEXT = {
  yes: "The data may be used for the development of commercial products, subject to appropriate legal and benefit-sharing agreements being in place.",
  no: "The data may not be used for the development of commercial products or outputs."
}

const ONWARD_TEXT = {
  yes: "The data provided may be shared with any other researchers for further analysis or use.",
  no: "This dataset cannot be shared onward with anyone else. Other potential dataset users must approach the original data provider for permission to use this dataset."
}

const SHARE_ALIKE_TEXT = {
  yes: "Share-alike is required, which means that any onward sharing must have the same sharing license terms as the original terms of sharing, as defined by this license.",
  no: "Share-alike is not required, and any onward sharing can be done under different conditions to this license if preferred."
}

const MODIFICATION_TEXT = {
  yes: "Modified versions of the dataset, or dataset derivatives may be shared onward to others.",
  no: "Modified versions of the dataset, or dataset derivatives may not be shared onward. Only the original version of the dataset as received from the data provider may be shared onward."
}

const BENEFIT_TEXT = {
  yes: "There is a requirement for a benefit-sharing plan when using this dataset.",
  no: "No benefit-sharing agreement is required when using this dataset."
}

const COST_RECOVERY_TEXT = {
  yes: "There is a requirement for a cost-recovery payment when using this dataset.",
  no: "There is no cost recovery payment required for using this dataset."
}

const PUBLISHING_TEXT = {
  allowed: "Publication and dissemination of results from working with these data are permitted without restrictions.",
  embargo:
    "Any results from working with these data are permitted after an embargo period.",
  review:
    "Publication and dissemination of results from working with these data are permitted only after review and approval of the proposed publication by the data provider.",
  reporting:
    "Only descriptive reporting of aggregated data is allowed. Examples include, but are not limited to, inclusion in dashboards reporting numbers, or in monitoring and evaluation reporting of total numbers.",
  none: "No publications and dissemination of results from working with these data is permitted. Examples of publication and dissemination outputs that are not allowed include, but are not limited to, peer-reviewed publications, blogs, social media posts, public presentations and other public media."
}

const ATTRIBUTION_TEXT = {
  none: "The data provider does not need to be named or attributed in any way, and does not require authorship on any publications using these data.",
  attribution:
    "The data provider requires attribution on any outputs created using these data.",
  authorship:
    "The data provider requires authorship on any outputs published using these data."
}

const EXPIRY_TEXT = {
  dated: "This Data Sharing License expires on: $date.",
  none: "This Data Sharing License does not expire.",
  actions: "The following actions must be taken on expiry of this license: $actions"
}

const formatParagraphs = (items: string[]) =>
  items.filter(Boolean).map(item => item.trim()).join("\n\n")

const toRoman = (value: number) => {
  if (value <= 0) return ""
  const lookup: Array<[number, string]> = [
    [1000, "m"],
    [900, "cm"],
    [500, "d"],
    [400, "cd"],
    [100, "c"],
    [90, "xc"],
    [50, "l"],
    [40, "xl"],
    [10, "x"],
    [9, "ix"],
    [5, "v"],
    [4, "iv"],
    [1, "i"]
  ]
  let remaining = value
  let result = ""
  lookup.forEach(([amount, roman]) => {
    while (remaining >= amount) {
      result += roman
      remaining -= amount
    }
  })
  return result
}

const formatList = (items: string[], style: "ordered" | "unordered") =>
  items
    .filter(Boolean)
    .map((item, index) => {
      const marker = style === "ordered" ? `${index + 1}.` : "-"
      return `${marker} ${item.trim()}`
    })
    .join("\n")

const formatSubList = (
  heading: string,
  items: string[],
  style: "roman" | "decimal",
  listStyle: "ordered" | "unordered"
) => {
  const sub = items.filter(Boolean)
  if (!sub.length) return heading
  const formatIndex = (index: number) => {
    if (listStyle === "unordered") return "-"
    return style === "roman" ? `${toRoman(index + 1)}.` : `${index + 1}.`
  }
  const indent = "    "
  return `${heading}\n${sub
    .map((item, index) => `${indent}${formatIndex(index)} ${item.trim()}`)
    .join("\n")}`
}

const titleFromCode = (code?: string) => "Data Sharing License"
  // code && code.trim()
    // ? `Data Sharing License 1.0-${code}`
    // : "Data Sharing License 1.0"

export const generateLicenseMarkdown = (
  normalizedData: NormalizedLicenseData,
  options: GenerateLicenseMarkdownOptions = {}
): string => {
  const licenseCode = 'xxx'//options.licenseCode ?? buildLicenseCode(normalizedData, "sharing")
  const subListStyle = options.subListStyle ?? "roman"
  const listStyle = options.listStyle ?? "ordered"
  const licenseTitle = titleFromCode(licenseCode)
  const generatedDate = normalizedData.generatedDate || new Date().toISOString().slice(0, 10)

  const datasetLines: string[] = []
  if (normalizedData.datasetTitle) {
    datasetLines.push(`**Dataset title:** ${normalizedData.datasetTitle}`)
  }
  if (normalizedData.datasetDescription) {
    datasetLines.push(`**Description:** ${normalizedData.datasetDescription}`)
  }
  if (normalizedData.yearGenerated) {
    datasetLines.push(`**Year generated:** ${normalizedData.yearGenerated}`)
  }
  if (normalizedData.identifiersList.length) {
    datasetLines.push(
      `**Identifiers/links:** ${normalizedData.identifiersList.join("; ")}`
    )
  }

  const terms: string[] = []

  if (normalizedData.hasExpiry && normalizedData.expiryDate) {
    terms.push(EXPIRY_TEXT.dated.replace("$date", normalizedData.expiryDate))
  } else if (!normalizedData.hasExpiry) {
    terms.push(EXPIRY_TEXT.none)
  }
  if (normalizedData.expiryActions) {
    terms.push(EXPIRY_TEXT.actions.replace("$actions", normalizedData.expiryActions))
  }

  normalizedData.accessMode?.forEach(mode => {
    const text = ACCESS_TEXT[mode]
    if (text) terms.push(text)
  })

  terms.push(COMMERCIAL_TEXT[normalizedData.commercialUse])
  terms.push(ONWARD_TEXT[normalizedData.onwardSharing])

  if (normalizedData.onwardSharing === "yes") {
    terms.push(SHARE_ALIKE_TEXT[normalizedData.shareAlike])
    terms.push(MODIFICATION_TEXT[normalizedData.modifications])
  }

  if (normalizedData.benefitSharing === "yes") {
    const detailParts = [
      normalizedData.benefitSharingContactEmail
        ? `Contact: ${normalizedData.benefitSharingContactEmail}`
        : null,
      normalizedData.benefitSharingContactUrl
        ? `More info: ${normalizedData.benefitSharingContactUrl}`
        : null,
      normalizedData.benefitSharingActivities
    ].filter(Boolean) as string[]

    terms.push(
      formatSubList(
        BENEFIT_TEXT.yes,
        detailParts.length ? detailParts : ["Details will be provided by the data provider."],
        subListStyle,
        listStyle
      )
    )
  } else {
    terms.push(BENEFIT_TEXT.no)
  }

  if (normalizedData.costRecovery === "yes") {
    const costDetails = [
      normalizedData.costRecoveryContactEmail
        ? `Contact: ${normalizedData.costRecoveryContactEmail}`
        : null,
      normalizedData.costRecoveryContactUrl
        ? `More info: ${normalizedData.costRecoveryContactUrl}`
        : null,
      normalizedData.costRecoveryDetails
    ].filter(Boolean) as string[]

    terms.push(
      formatSubList(
        COST_RECOVERY_TEXT.yes,
        costDetails.length ? costDetails : ["Details will be provided by the data provider."],
        subListStyle,
        listStyle
      )
    )
  } else {
    terms.push(COST_RECOVERY_TEXT.no)
  }

  if (normalizedData.publishingPermitted === "no") {
    terms.push(PUBLISHING_TEXT.none)
  } else {
    const publishingClauses: string[] = []
    const reqs = normalizedData.publishingRequirements || {}

    if (reqs.embargo) publishingClauses.push(PUBLISHING_TEXT.embargo)
    if (reqs.review) publishingClauses.push(PUBLISHING_TEXT.review)
    if (reqs.reporting) publishingClauses.push(PUBLISHING_TEXT.reporting)

    if (reqs.reporting && normalizedData.reportingRequirements === "yes" && normalizedData.reportingTypesPermitted?.trim()) {
      publishingClauses.push(
        `Examples of aggregate reporting allowed/not allowed: ${normalizedData.reportingTypesPermitted.trim()}.`
      )
    } else if (reqs.reporting && normalizedData.reportingRequirements === "yes") {
      publishingClauses.push(
        "More detail on permitted aggregate reporting will be provided by the data provider."
      )
    }

    if (publishingClauses.length) {
      terms.push(
        formatSubList(
          "Publication and dissemination are permitted with requirements:",
          publishingClauses,
          subListStyle,
          listStyle
        )
      )
    } else {
      terms.push(PUBLISHING_TEXT.allowed)
    }
  }

  if (normalizedData.attributionRequired === "no") {
    terms.push(ATTRIBUTION_TEXT.none)
  } else {
    const attributionClauses: string[] = []
    if (normalizedData.attributionRequirements?.attribution) {
      attributionClauses.push(ATTRIBUTION_TEXT.attribution)
    }
    if (normalizedData.attributionRequirements?.authorship) {
      attributionClauses.push(ATTRIBUTION_TEXT.authorship)
    }
    const attributionSummary =
      attributionClauses.length > 1
        ? formatSubList("Attribution is required:", attributionClauses, subListStyle, listStyle)
        : attributionClauses[0] ?? ATTRIBUTION_TEXT.attribution

    terms.push(attributionSummary)
  }

  const confirmations: string[] = []
  if (normalizedData.acceptedAuthorization) {
    confirmations.push(
      "The data provider confirmed they are responsible for the data and have authority to create this Data Sharing License."
    )
  }

  if (normalizedData.acceptedMutability) {
    confirmations.push(
      normalizedData.hasExpiry
        ? "When a dataset is shared with a recipient under this license, the license remains valid until the specified end date. Until that date, it cannot be revoked or changed for that recipient."
        : "When a dataset is shared with a recipient under this license, the license cannot later be revoked or changed for that sharing agreement: the recipient keeps the right to use the dataset even if the dataset is later shared elsewhere under a different license or Terms and Conditions."
    )
  }

  if (normalizedData.acceptedTerms) {
    confirmations.push(
      "The data provider has read and understands the terms of the Data Sharing License."
    )
  }

  const sections: string[] = [
    `# ${licenseTitle}`,
    `_Generated on ${generatedDate}_`
  ]

  if (datasetLines.length) {
    sections.push("## Dataset", formatParagraphs(datasetLines))
  }

  if (terms.length) {
    sections.push("## Terms", formatList(terms, listStyle))
  }

  if (confirmations.length) {
    sections.push("## Confirmations", formatList(confirmations, listStyle))
  }

  return sections.join("\n\n").trim()
}
